/**
 * AbaMentoria — Leads da página pública /mentoria
 *
 * Exibe, para a consultora logada, apenas os leads que ela indicou
 * (via ?c=usuario). Para a administradora (Mônica), exibe todos os
 * leads de todas as consultoras, com coluna e filtro por consultora.
 */
import { useState, useEffect, useMemo } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {HeartHandshake, Download, Eye, X, MessageCircleDashed as MessageCircle} from 'lucide-react'

interface MentoriaLead {
  _id: string
  nome: string
  whatsapp: string
  email: string
  atuacaoProfissional: string
  comoSeDefine: string
  objetivoPrincipal: string
  consultora: string
  status: string
  dataInscricao: string
  createdAt: string
}

interface Props {
  /** Usuário da consultora logada (ou 'monica' para admin) */
  consultoraAdmin: string
  /** Se true, vê todos os leads; se false, vê apenas os seus */
  isAdmin?: boolean
}

const STATUS_OPCOES = ['Novo lead', 'Contatado', 'Acesso liberado', 'Participando', 'Convertido', 'Não qualificado']

const CORES_STATUS: Record<string, { bg: string; text: string }> = {
  'Novo lead': { bg: '#E6F1FB', text: '#185FA5' },
  'Contatado': { bg: '#FAEEDA', text: '#854F0B' },
  'Acesso liberado': { bg: '#F3E8FF', text: '#6B21A8' },
  'Participando': { bg: '#FEF3C7', text: '#92400E' },
  'Convertido': { bg: '#D8F3DC', text: '#2D6A4F' },
  'Não qualificado': { bg: '#F3F4F6', text: '#4B5563' },
}

export default function AbaMentoria({ consultoraAdmin, isAdmin = false }: Props) {
  const [leads, setLeads] = useState<MentoriaLead[]>([])
  const [carregando, setCarregando] = useState(true)
  const [selecionado, setSelecionado] = useState<MentoriaLead | null>(null)

  const [filtroStatus, setFiltroStatus] = useState('todos')
  const [filtroDefine, setFiltroDefine] = useState('todos')
  const [filtroObjetivo, setFiltroObjetivo] = useState('todos')
  const [filtroConsultora, setFiltroConsultora] = useState('todos')
  const [filtroDataInicio, setFiltroDataInicio] = useState('')
  const [filtroDataFim, setFiltroDataFim] = useState('')

  useEffect(() => {
    void carregarLeads()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function carregarLeads() {
    setCarregando(true)
    try {
      const filtro = isAdmin ? {} : { consultora: consultoraAdmin }
      const res = await lumi.entities.mentoria.list({ filter: filtro, sort: { dataInscricao: -1 }, limit: 500 })
      setLeads(res?.list ?? [])
    } catch (e) {
      console.error('[mentoria] erro ao carregar leads', e)
      toast.error('Erro ao carregar inscrições da Mentoria')
    } finally {
      setCarregando(false)
    }
  }

  async function atualizarStatus(id: string, novoStatus: string) {
    try {
      await lumi.entities.mentoria.update(id, { status: novoStatus, updatedAt: new Date().toISOString() })
      toast.success('Status atualizado!')
      setLeads(prev => prev.map(l => (l._id === id ? { ...l, status: novoStatus } : l)))
      if (selecionado?._id === id) setSelecionado(prev => (prev ? { ...prev, status: novoStatus } : prev))
    } catch (e) {
      console.error('[mentoria] erro ao atualizar status', e)
      toast.error('Erro ao atualizar status')
    }
  }

  const consultorasUnicas = useMemo(() => {
    const set = new Set<string>()
    leads.forEach(l => set.add(l.consultora || 'geral'))
    return Array.from(set).sort()
  }, [leads])

  const leadsFiltrados = useMemo(() => {
    return leads.filter(l => {
      const mStatus = filtroStatus === 'todos' || l.status === filtroStatus
      const mDefine = filtroDefine === 'todos' || l.comoSeDefine === filtroDefine
      const mObjetivo = filtroObjetivo === 'todos' || l.objetivoPrincipal === filtroObjetivo
      const mConsultora = !isAdmin || filtroConsultora === 'todos' || (l.consultora || 'geral') === filtroConsultora
      const dataRef = l.dataInscricao || l.createdAt
      const mInicio = !filtroDataInicio || (dataRef && dataRef >= filtroDataInicio)
      const mFim = !filtroDataFim || (dataRef && dataRef <= `${filtroDataFim}T23:59:59.999Z`)
      return mStatus && mDefine && mObjetivo && mConsultora && mInicio && mFim
    })
  }, [leads, filtroStatus, filtroDefine, filtroObjetivo, filtroConsultora, filtroDataInicio, filtroDataFim, isAdmin])

  const exportarCSV = () => {
    const headers = isAdmin
      ? ['Nome', 'WhatsApp', 'E-mail', 'Atuação profissional', 'Como se define', 'Objetivo principal', 'Data de inscrição', 'Consultora', 'Status']
      : ['Nome', 'WhatsApp', 'E-mail', 'Atuação profissional', 'Como se define', 'Objetivo principal', 'Data de inscrição', 'Status']
    const rows = leadsFiltrados.map(l => {
      const base = [
        l.nome,
        l.whatsapp,
        l.email,
        l.atuacaoProfissional,
        l.comoSeDefine,
        l.objetivoPrincipal,
        l.dataInscricao ? new Date(l.dataInscricao).toLocaleString('pt-BR') : '',
      ]
      if (isAdmin) base.push(l.consultora || 'geral')
      base.push(l.status)
      return base
    })
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n')
    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', `mentoria_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-1 flex items-center gap-2">
          <HeartHandshake className="w-6 h-6 text-verde-escuro" />
          Mentoria
        </h2>
        <p className="text-sm text-texto-suave">
          {isAdmin
            ? 'Todas as inscrições recebidas pela página pública /mentoria, de todas as consultoras.'
            : 'Inscrições recebidas pelo seu link personalizado da página /mentoria.'}
        </p>
      </div>

      {/* Métricas rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {STATUS_OPCOES.map(status => (
          <div key={status} className="bg-white p-4 rounded-xl shadow-md text-center">
            <div className="text-2xl font-bold mb-1" style={{ color: CORES_STATUS[status]?.text || '#2C2C2A' }}>
              {leads.filter(l => l.status === status).length}
            </div>
            <div className="text-xs text-texto-suave">{status}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-xl shadow-md mb-6">
        <div className={`grid grid-cols-1 md:grid-cols-3 ${isAdmin ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-4`}>
          <div>
            <label className="block text-xs text-texto-suave mb-1">Status</label>
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
              className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm">
              <option value="todos">Todos</option>
              {STATUS_OPCOES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-texto-suave mb-1">Como se define</label>
            <select value={filtroDefine} onChange={e => setFiltroDefine(e.target.value)}
              className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm">
              <option value="todos">Todas</option>
              <option value="Já atuo na área de bem-estar">Já atuo na área de bem-estar</option>
              <option value="Estou começando">Estou começando</option>
              <option value="Ainda não atuo mas tenho interesse">Ainda não atuo mas tenho interesse</option>
              <option value="Atuo em outra área e quero migrar">Atuo em outra área e quero migrar</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-texto-suave mb-1">Objetivo principal</label>
            <select value={filtroObjetivo} onChange={e => setFiltroObjetivo(e.target.value)}
              className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm">
              <option value="todos">Todos</option>
              <option value="Quero ter mais clientes">Quero ter mais clientes</option>
              <option value="Quero uma nova fonte de renda">Quero uma nova fonte de renda</option>
              <option value="Quero profissionalizar meu trabalho">Quero profissionalizar meu trabalho</option>
              <option value="Quero começar do zero">Quero começar do zero</option>
            </select>
          </div>
          {isAdmin && (
            <div>
              <label className="block text-xs text-texto-suave mb-1">Consultora</label>
              <select value={filtroConsultora} onChange={e => setFiltroConsultora(e.target.value)}
                className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm">
                <option value="todos">Todas</option>
                {consultorasUnicas.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-texto-suave mb-1">Período (de)</label>
            <input type="date" value={filtroDataInicio} onChange={e => setFiltroDataInicio(e.target.value)}
              className="w-full px-3 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm" />
          </div>
          <div>
            <label className="block text-xs text-texto-suave mb-1">Período (até)</label>
            <div className="flex gap-2">
              <input type="date" value={filtroDataFim} onChange={e => setFiltroDataFim(e.target.value)}
                className="flex-1 px-3 py-2 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none text-sm" />
              <button onClick={exportarCSV}
                className="flex items-center justify-center gap-1.5 bg-verde-escuro text-white px-3 py-2 rounded-xl hover:bg-opacity-90 transition-all text-sm font-medium flex-shrink-0"
                title="Exportar CSV">
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {carregando ? (
          <div className="p-8 text-center text-texto-suave">Carregando...</div>
        ) : leadsFiltrados.length === 0 ? (
          <div className="p-10 text-center">
            <HeartHandshake className="w-10 h-10 text-texto-suave mx-auto mb-3" />
            <p className="text-texto-suave">Nenhuma inscrição na Mentoria encontrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-bege-fundo">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Nome</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">WhatsApp</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">E-mail</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Atuação profissional</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Como se define</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Objetivo principal</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Inscrição</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Consultora</th>}
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bege-medio">
                {leadsFiltrados.map(l => (
                  <tr key={l._id} className="hover:bg-bege-fundo">
                    <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{l.nome}</td>
                    <td className="px-4 py-3 text-sm">
                      <a href={`https://wa.me/${(l.whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                        className="text-verde-escuro hover:underline">{l.whatsapp}</a>
                    </td>
                    <td className="px-4 py-3 text-sm text-texto-suave">{l.email}</td>
                    <td className="px-4 py-3 text-sm text-texto-suave max-w-[180px] truncate">{l.atuacaoProfissional}</td>
                    <td className="px-4 py-3 text-sm text-texto-suave max-w-[180px]">{l.comoSeDefine}</td>
                    <td className="px-4 py-3 text-sm text-texto-suave max-w-[180px]">{l.objetivoPrincipal}</td>
                    <td className="px-4 py-3 text-sm text-texto-suave whitespace-nowrap">
                      {l.dataInscricao ? new Date(l.dataInscricao).toLocaleDateString('pt-BR') : '—'}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                          {l.consultora || 'geral'}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm">
                      <div className="relative inline-block">
                        <select
                          value={l.status}
                          onChange={e => atualizarStatus(l._id, e.target.value)}
                          className="appearance-none px-3 py-1.5 pr-7 rounded-full text-xs font-medium cursor-pointer focus:outline-none"
                          style={{ backgroundColor: CORES_STATUS[l.status]?.bg || '#F3F4F6', color: CORES_STATUS[l.status]?.text || '#4B5563' }}
                        >
                          {STATUS_OPCOES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <button onClick={() => setSelecionado(l)}
                        className="p-2 text-verde-escuro hover:bg-verde-muito-claro rounded-lg transition-colors" title="Ver detalhes">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de detalhes */}
      {selecionado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#2D6A4F] text-white px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
              <h2 className="text-xl font-cormorant font-semibold flex items-center gap-2">
                <HeartHandshake className="w-5 h-5" /> {selecionado.nome}
              </h2>
              <button onClick={() => setSelecionado(null)} className="text-white hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { label: 'WhatsApp', valor: selecionado.whatsapp },
                { label: 'E-mail', valor: selecionado.email },
                { label: 'Atuação profissional', valor: selecionado.atuacaoProfissional },
                { label: 'Como se define hoje', valor: selecionado.comoSeDefine },
                { label: 'Objetivo principal', valor: selecionado.objetivoPrincipal },
                { label: 'Data de inscrição', valor: selecionado.dataInscricao ? new Date(selecionado.dataInscricao).toLocaleString('pt-BR') : '—' },
                ...(isAdmin ? [{ label: 'Consultora', valor: selecionado.consultora || 'geral' }] : []),
              ].map(({ label, valor }) => (
                <div key={label}>
                  <div className="text-xs text-texto-suave mb-0.5 font-medium">{label}</div>
                  <div className="text-sm text-texto-escuro">{valor || '—'}</div>
                </div>
              ))}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-bege-medio">
                <a
                  href={`https://wa.me/${(selecionado.whatsapp || '').replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white rounded-xl text-sm font-medium hover:bg-[#1ea34e] transition-all"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
